<template>
  <div class="ncrm-mailjet-events">
    <!-- Filters bar -->
    <div class="ncrm-mailjet-events__filters mb-3">
      <v-select
        v-model="eventTypeFilter"
        :items="eventTypeOptions"
        :label="$t('ncrm.integrations.mailjet.events.filters.eventType')"
        hide-details
        clearable
        style="max-width: 200px;"
        @update:model-value="onFilterChange"
      />
      <v-select
        v-model="sinceHours"
        :items="windowOptions"
        :label="$t('ncrm.integrations.mailjet.events.filters.window')"
        hide-details
        clearable
        style="max-width: 200px;"
        @update:model-value="onFilterChange"
      />
      <v-text-field
        v-model="searchQuery"
        :label="$t('ncrm.integrations.mailjet.events.filters.search')"
        prepend-inner-icon="mdi-magnify"
        hide-details
        clearable
        style="max-width: 320px;"
        @update:model-value="onSearchInput"
      />
      <v-spacer />
      <span class="text-caption text-medium-emphasis">
        {{ $t("ncrm.integrations.mailjet.events.totalLabel", { total }) }}
      </span>
      <v-btn
        variant="text"
        size="small"
        :loading="loading"
        :disabled="!accountId"
        @click="fetchEvents"
      >
        <v-icon start size="16">mdi-refresh</v-icon>
        {{ $t("ncrm.integrations.mailjet.events.refresh") }}
      </v-btn>
    </div>

    <v-data-table-server
      :headers="headers"
      :items="rows"
      :items-length="total"
      :loading="loading"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :items-per-page-options="[25, 50, 100, 200]"
      density="comfortable"
      class="elevation-0"
      item-key="id"
      @click:row="openPayload"
    >
      <template #item.event_type="{ item }">
        <v-chip
          :color="eventTypeColor(item.event_type)"
          size="x-small"
        >
          {{ item.event_type }}
        </v-chip>
      </template>
      <template #item.event_at="{ item }">
        <span class="text-caption">{{ formatDate(item.event_at) }}</span>
      </template>
      <template #item.contact_email="{ item }">
        <span v-if="item.contact_email" class="text-body-2">{{ item.contact_email }}</span>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #item.campaign_subject="{ item }">
        <span v-if="item.campaign_subject" class="text-body-2">{{ truncate(item.campaign_subject, 60) }}</span>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #item.mailjet_message_id="{ item }">
        <code v-if="item.mailjet_message_id" class="text-caption">{{ item.mailjet_message_id }}</code>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-2">
          {{ $t("ncrm.integrations.mailjet.events.empty") }}
        </v-alert>
      </template>
    </v-data-table-server>

    <!-- Payload drill-down dialog -->
    <v-dialog v-model="payloadDialog.show" max-width="720" scrollable>
      <v-card v-if="payloadDialog.event">
        <v-card-title class="d-flex align-center flex-wrap ga-2">
          <v-chip
            :color="eventTypeColor(payloadDialog.event.event_type)"
          >
            {{ payloadDialog.event.event_type }}
          </v-chip>
          <span class="text-body-1">
            {{ payloadDialog.event.contact_email || $t("ncrm.integrations.mailjet.events.unknownContact") }}
          </span>
          <v-spacer />
          <span class="text-caption text-medium-emphasis">
            {{ formatDate(payloadDialog.event.event_at) }}
          </span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <v-list density="comfortable">
            <v-list-item v-if="payloadDialog.event.mailjet_message_id">
              <v-list-item-title>{{ $t("ncrm.integrations.mailjet.events.fields.messageId") }}</v-list-item-title>
              <v-list-item-subtitle>
                <code>{{ payloadDialog.event.mailjet_message_id }}</code>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="payloadDialog.event.campaign_subject">
              <v-list-item-title>{{ $t("ncrm.integrations.mailjet.events.fields.campaign") }}</v-list-item-title>
              <v-list-item-subtitle>{{ payloadDialog.event.campaign_subject }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="payloadDialog.event.contact_id">
              <v-list-item-title>{{ $t("ncrm.integrations.mailjet.events.fields.localContactId") }}</v-list-item-title>
              <v-list-item-subtitle>{{ payloadDialog.event.contact_id }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
          <v-divider />
          <div class="pa-3">
            <div class="text-caption text-medium-emphasis mb-1">
              {{ $t("ncrm.integrations.mailjet.events.rawPayload") }}
            </div>
            <pre class="ncrm-mailjet-events__payload">{{ formattedPayload }}</pre>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="payloadDialog.show = false">
            {{ $t("ncrm.integrations.mailjet.dialog.actions.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

// Color hints per event type — purely cosmetic, matches the broader
// success/warning/error palette.
const EVENT_COLORS = {
  sent: "info",
  open: "success",
  click: "success",
  unsub: "warning",
  spam: "error",
  blocked: "error",
  hardbounce: "error",
  softbounce: "warning",
  bounce: "warning",
};

export default {
  name: "NcrmMailjetEventsTab",
  props: {
    account: { type: Object, required: true },
  },
  emits: ["notify"],
  data() {
    return {
      rows: [],
      total: 0,
      loading: false,
      page: 1,
      itemsPerPage: 50,
      eventTypeFilter: null,
      eventTypeOptions: [],
      sinceHours: null,
      searchQuery: "",
      searchTimer: null,
      payloadDialog: { show: false, event: null },
    };
  },
  computed: {
    accountId() {
      return this.account?.id || null;
    },
    headers() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.events.headers.eventType"), key: "event_type" },
        { title: this.$t("ncrm.integrations.mailjet.events.headers.eventAt"), key: "event_at" },
        { title: this.$t("ncrm.integrations.mailjet.events.headers.contact"), key: "contact_email" },
        { title: this.$t("ncrm.integrations.mailjet.events.headers.campaign"), key: "campaign_subject" },
        { title: this.$t("ncrm.integrations.mailjet.events.headers.messageId"), key: "mailjet_message_id", sortable: false },
      ];
    },
    windowOptions() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.events.window.h1"),  value: 1 },
        { title: this.$t("ncrm.integrations.mailjet.events.window.h24"), value: 24 },
        { title: this.$t("ncrm.integrations.mailjet.events.window.d7"),  value: 168 },
        { title: this.$t("ncrm.integrations.mailjet.events.window.d30"), value: 720 },
      ];
    },
    formattedPayload() {
      const p = this.payloadDialog.event?.payload_json;
      if (!p) return "";
      try {
        return JSON.stringify(typeof p === "string" ? JSON.parse(p) : p, null, 2);
      } catch (_) {
        return String(p);
      }
    },
  },
  watch: {
    accountId: {
      handler(id) {
        if (!id) {
          this.rows = [];
          this.total = 0;
          this.eventTypeOptions = [];
          return;
        }
        this.page = 1;
        this.fetchEventTypes();
        this.fetchEvents();
      },
      immediate: true,
    },
    page() { this.fetchEvents(); },
    itemsPerPage() { this.page = 1; this.fetchEvents(); },
  },
  methods: {
    formatDate(value) {
      if (!value) return "—";
      try {
        return formatDisplayDateTime(value);
      } catch (_) {
        return String(value);
      }
    },
    truncate(text, max = 60) {
      if (!text) return "";
      const s = String(text);
      return s.length > max ? `${s.slice(0, max)}…` : s;
    },
    eventTypeColor(type) {
      return EVENT_COLORS[type] || "default";
    },
    extractError(error, fallbackKey) {
      return error?.response?.data?.error || error?.message || this.$t(fallbackKey);
    },
    notify(color, message) {
      this.$emit("notify", { color, message });
    },

    onFilterChange() {
      this.page = 1;
      this.fetchEvents();
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.fetchEvents();
      }, 300);
    },

    async fetchEventTypes() {
      if (!this.accountId) return;
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/events/types`
        );
        const types = Array.isArray(data?.types) ? data.types : [];
        this.eventTypeOptions = types.map((t) => ({ title: t, value: t }));
      } catch (_) {
        this.eventTypeOptions = [];
      }
    },

    async fetchEvents() {
      if (!this.accountId) return;
      this.loading = true;
      try {
        const params = {
          limit: this.itemsPerPage,
          offset: (this.page - 1) * this.itemsPerPage,
        };
        if (this.eventTypeFilter) params.eventType = this.eventTypeFilter;
        if (this.sinceHours) params.sinceHours = this.sinceHours;
        const trimmed = (this.searchQuery || "").trim();
        if (trimmed) params.search = trimmed;
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/events`,
          { params }
        );
        this.rows = Array.isArray(data?.rows) ? data.rows : [];
        this.total = Number(data?.total || 0);
      } catch (error) {
        this.notify(
          "error",
          this.extractError(error, "ncrm.integrations.mailjet.events.errors.list")
        );
      } finally {
        this.loading = false;
      }
    },

    openPayload(_event, row) {
      const item = row?.item || row;
      if (!item) return;
      this.payloadDialog = { show: true, event: item };
    },
  },
};
</script>

<style scoped>
.ncrm-mailjet-events__filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.ncrm-mailjet-events__payload {
  background: var(--app-surface-2, rgba(0, 0, 0, 0.04));
  padding: 12px;
  border-radius: 6px;
  font-size: 12px;
  max-height: 400px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
